import { inject, observer } from 'mobx-react'
import styled from '@emotion/styled'

import { RailIcon } from '../../components'
import { ReactComponent as MenuIcon } from '../../assets/icons/ActionMenuOpen.svg'
import { ReactComponent as MenuCloseIcon } from '../../assets/icons/ActionMenuClose.svg'
import { ReactComponent as PlanIcon } from '../../assets/icons/IpmIconPlan.svg'
import ChatHistoryItem from './ChatHistoryItem'
import { CHAT_MOBILE } from './styled'

const Bar = styled.div`
  flex: 0 0 auto;
  /* Above the chat content so the collapsed on-hover labels paint over it. */
  position: relative;
  z-index: 5;
  width: ${p => (p.expanded ? '300px' : '72px')};
  border-right: 1px solid ${p => p.theme.containerBorderColor ?? '#e6e9ea'};
  background: ${p => p.theme.sideBarBgColor ?? '#ffffff'};
  box-shadow: 0 2px 2px 0 rgba(182, 192, 196, 0.15);
  display: flex;
  flex-direction: column;
  transition: width 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  /* Clip content during the expand reveal, but when collapsed let the on-hover
     labels ("Open sidebar" / "New chat") escape the 72px rail to the right. */
  overflow: ${p => (p.expanded ? 'hidden' : 'visible')};

  ${CHAT_MOBILE} {
    display: none;
  }
`

// Horizontal padding is 0 so the toggle's fixed 72px cell starts at the Bar's
// edge, keeping the toggle icon in the exact same spot in both states.
const RailHeader = styled.div`
  display: flex;
  align-items: center;
  padding: 6px 0;
`

// Own row beneath the collapse toggle; grows to fill the rail with a 15px gutter
// on each side.
const StartChatButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin: 0 15px;
  padding: 5px 20px;
  border-radius: 76px;
  border: 1px solid ${p => p.theme.lightestGray ?? '#7a8e96'};
  background: transparent;
  color: ${p => p.theme.mediumGray ?? '#4a606a'};
  font-size: 14px;
  font-weight: 400;
  cursor: pointer;
  transition: 0.2s;
  & svg {
    display: block;
    width: 18px;
    height: 18px;
  }
  /* The icon's inner group has a hardcoded fill; retint it to match the text. */
  & svg g g {
    fill: currentColor;
  }
  &:hover {
    background: ${p => p.theme.ternaryColor ?? '#e7f3f9'};
  }
  &:disabled {
    cursor: default;
    opacity: 0.5;
    background: transparent;
  }
`

const RecentsLabel = styled.div`
  padding: 30px 16px 8px 16px;
  color: ${p => p.theme.lightestGray ?? '#7a8e96'};
  font-size: 13px;
  font-weight: 700;
`

const List = styled.div`
  flex: 1 1 auto;
  overflow-y: auto;
  padding: 4px 10px 16px;
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const ChatHistorySidebar = ({ store }) => {
  const expanded = store.heyNicSidebarExpanded
  const chats = store.sortedChatMetadatas
  const activeId = store.activeChat?.id ?? null
  // While a response streams, chat switching / creating / deleting is locked so
  // the user can't abort the in-flight stream (see Chat.js closeStream callers).
  const streaming = store.responseLoading

  const handleNewChat = () => {
    if (streaming) return
    store.startNewChat()
  }

  const handleDelete = async id => {
    if (streaming) return
    await store.removeChat(id)
  }

  if (!expanded) {
    return (
      <Bar expanded={false}>
        <RailHeader>
          <RailIcon cellWidth='72px' label='Open sidebar' onClick={store.toggleHeyNicSidebar}>
            <MenuIcon />
          </RailIcon>
        </RailHeader>
        <RailIcon cellWidth='72px' label='New chat' onClick={handleNewChat} disabled={streaming}>
          <PlanIcon />
        </RailIcon>
      </Bar>
    )
  }

  return (
    <Bar expanded>
      <RailHeader>
        <RailIcon cellWidth='72px' label='Close sidebar' onClick={store.toggleHeyNicSidebar}>
          <MenuCloseIcon />
        </RailIcon>
      </RailHeader>

      <StartChatButton type='button' onClick={handleNewChat} disabled={streaming}>
        <PlanIcon /> New chat
      </StartChatButton>

      <RecentsLabel>Recents</RecentsLabel>
      <List>
        {chats.map(meta => (
          <ChatHistoryItem
            key={meta.id}
            metadata={meta}
            selected={meta.id === activeId}
            disabled={streaming}
            onSelect={store.openChat}
            onDelete={handleDelete}
          />
        ))}
      </List>
    </Bar>
  )
}

export default inject('store')(observer(ChatHistorySidebar))
